import { readFileSync, writeFileSync, existsSync, mkdirSync } from "node:fs";
import { resolve, dirname } from "node:path";

export type BuildMode = "launch" | "worktree" | "manual";

export interface ProjectConfig {
  project: string;
  intakeDir?: string;
  buildMode?: BuildMode;
  anthropicAdminKey?: string;
  storeType?: "file" | "convex";
  createdAt: string;
}

/**
 * Read the project config from .fathom/config.json.
 * Returns null if no config exists.
 */
export function readProjectConfig(): ProjectConfig | null {
  const configPath = resolve(process.cwd(), ".fathom", "config.json");
  if (!existsSync(configPath)) return null;
  try {
    return JSON.parse(readFileSync(configPath, "utf-8"));
  } catch {
    return null;
  }
}

/**
 * Update fields in the project config. Creates the file if it doesn't exist.
 * Always writes to .fathom/.
 */
export function updateProjectConfig(updates: Partial<ProjectConfig>): void {
  const existing = readProjectConfig() ?? {
    project: "unnamed",
    createdAt: new Date().toISOString(),
  };
  const merged = { ...existing, ...updates };
  const configPath = resolve(process.cwd(), ".fathom", "config.json");
  mkdirSync(dirname(configPath), { recursive: true });
  writeFileSync(configPath, JSON.stringify(merged, null, 2) + "\n");
}

/**
 * Get the project name, returning "unnamed" if no config exists.
 */
export function getProjectName(): string {
  return readProjectConfig()?.project ?? "unnamed";
}

/**
 * Get the configured intake output directory.
 * Defaults to "intake-specs" in the project root.
 */
export function getIntakeDir(): string {
  return readProjectConfig()?.intakeDir ?? "intake-specs";
}
